import moment from 'moment';

/**
 * Formatea una fecha sin aplicar conversión de zona horaria
 * Útil para fechas que vienen solo en formato YYYY-MM-DD
 * @param {string} date - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha en formato DD-MM-YYYY
 */
export const formatDateOnly = (date) => {
    if (!date) return '';

    // Si la fecha viene en formato YYYY-MM-DD (solo fecha, sin hora)
    // no aplicamos conversión de zona horaria para evitar cambios de día
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
    }

    // Para fechas con hora, aplicar la lógica original
    return moment(moment.utc(date).toDate()).local().format('DD-MM-YYYY');
};

/**
 * Formatea una fecha UTC a formato local legible
 * @param {string|Date} date - Fecha en formato UTC
 * @returns {string} Fecha en formato DD-MM-YYYY
 */
export const formatDate = (date) => {
    return moment(moment.utc(date).toDate()).local().format('DD-MM-YYYY');
};

/**
 * Formatea una fecha UTC a formato local con hora
 * @param {string|Date} date - Fecha en formato UTC
 * @returns {string} Fecha y hora en formato DD-MM-YYYY HH:mm
 */
export const formatDateAndTime = (date) => {
    return moment(moment.utc(date).toDate()).local().format('DD-MM-YYYY LT');
};

/**
 * Formatea una fecha para inputs datetime-local de HTML5
 * @param {string} dateString - Cadena de fecha ISO o compatible
 * @returns {string} Fecha en formato YYYY-MM-DDTHH:mm para datetime-local
 */
export const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parsea una fecha para DatePicker sin aplicar conversión de zona horaria
 * si viene en formato YYYY-MM-DD (solo fecha)
 * @param {string} date - Fecha en cualquier formato
 * @returns {moment.Moment|null} Objeto moment sin conversión de zona horaria para fechas de solo fecha
 */
export const parseDateForPicker = (date) => {
    if (!date) return null;

    // Si la fecha viene en formato YYYY-MM-DD (solo fecha, sin hora)
    // no aplicamos conversión de zona horaria para evitar cambios de día
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return moment(date, 'YYYY-MM-DD');
    }

    // Para fechas con hora, aplicar conversión UTC a local
    return moment.utc(date).local();
};

/**
 * Formatea una fecha para envío al servidor
 * Maneja correctamente fechas que ya están en formato YYYY-MM-DD y fechas UTC del datepicker
 * Esto ya que en el editar servicio, si no se entra manualmente al datepicker, la fecha se convierte incorrectamente
 * @param {string} dateValue - Valor de fecha (puede ser UTC string o YYYY-MM-DD)
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const formatDateForSending = (dateValue) => {
    if (!dateValue) return null;

    // Si ya está en formato YYYY-MM-DD (probablemente del servidor), devolverla tal como está
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateValue;
    }

    // Si contiene 'T' o 'Z', es una fecha ISO/UTC del datepicker
    if (typeof dateValue === 'string' && (dateValue.includes('T') || dateValue.includes('Z'))) {
        return moment.utc(dateValue).local().format('YYYY-MM-DD');
    }

    // Para otros casos, tratar como fecha local
    return moment(dateValue).format('YYYY-MM-DD');
};

/**
 * Formatea una fecha que viene del servidor como string "YYYY-MM-DD"
 * sin hacer conversiones de zona horaria, mostrándola tal como viene
 */
export const formatServerDate = (dateString) => {
    if (!dateString) return '--';

    // Para fechas que vienen como "2025-10-29"
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    }

    // Fallback para otros formatos
    return formatDate(dateString);
};

/**
 * Converts a date string in format "YYYY-MM" to "MonthName-YYYY" in Spanish
 * @param {string} dateString - Date string in format "YYYY-MM" (e.g., "2025-12")
 * @returns {string} Formatted string like "Diciembre-2025"
 */
export const formatMonthYear = (dateString) => {
    if (!dateString) return '';
    
    const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const [year, month] = dateString.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    
    return `${months[monthIndex]}-${year}`;
};

/**
 * Converts a date string in format "YYYY-MM" to "MM-YYYY"
 * @param {string} dateString - Date string in format "YYYY-MM" (e.g., "2025-12")
 * @returns {string} Formatted string like "12-2025"
 */
export const reverseMonthYear = (dateString) => {
    if (!dateString) return '';
    
    const [year, month] = dateString.split('-');
    return `${month}-${year}`;
};
